import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  TextField,
  Autocomplete,
  Button,
  Grid,
  Paper,
  Typography,
  Alert,
  CircularProgress,
  Chip,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  Switch,
  FormControlLabel
} from '@mui/material';
import {
  Search as SearchIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon
} from '@mui/icons-material';
import { db } from '../../firebase';
import {
  collection,
  doc,
  getDocs,
  addDoc,
  updateDoc,
  onSnapshot,
  query,
  where,
  orderBy,
  limit,
  increment,
  writeBatch,
  serverTimestamp,
  getDoc
} from 'firebase/firestore';

import { COLORS } from '../../theme/constants';

const { primary: PRIMARY, teal: TEAL, warning: AMBER, danger: DANGER, success: SUCCESS, info: INFO } = COLORS;

/**
 * Componente de Dispensación con Stepper de 4 pasos
 * Paso 1: Producto
 * Paso 2: Contexto de uso
 * Paso 3: Lote (FIFO automático)
 * Paso 4: Confirmación
 */
const DispenseForm = ({ 
  patientId, 
  referenceId, 
  referenceType = 'hospitalization',
  location = 'hospitalization',
  onDispenseComplete 
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);

  // Paso 1: Producto
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [products, setProducts] = useState([]);
  const [controlledWarning, setControlledWarning] = useState('');

  // Paso 2: Contexto
  const [form, setForm] = useState({
    referenceType: 'hospitalization',
    referenceId: '',
    patientId: patientId || '',
    doctorId: '',
    nurseId: '',
    purpose: 'medication',
    quantity: '',
    notes: '',
  });

  // Paso 3: Lote
  const [batches, setBatches] = useState([]);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [useOtherBatch, setUseOtherBatch] = useState(false);
  const [otherBatchReason, setOtherBatchReason] = useState('');

  const steps = ['Producto', 'Contexto', 'Lote', 'Confirmación'];

  // Búsqueda de productos
  const handleProductSearch = useCallback(async (searchValue) => {
    setSearchTerm(searchValue);
    if (searchValue.length < 2) {
      setProducts([]);
      return;
    }
    try {
      const q = query(
        collection(db, 'products'),
        where('isActive', '==', true),
        orderBy('name'),
        limit(20)
      );
      const snap = await getDocs(q);
      const results = snap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(p => 
          p.name.toLowerCase().includes(searchValue.toLowerCase()) ||
          p.genericName?.toLowerCase().includes(searchValue.toLowerCase())
        );
      setProducts(results);
    } catch (err) {
      console.error('Error buscando productos:', err);
    }
  }, []);

  // Cargar lotes del producto seleccionado
  const loadBatches = useCallback(async (productId) => {
    try {
      const q = query(
        collection(db, 'batches'),
        where('productId', '==', productId),
        where('location', '==', location),
        where('isActive', '==', true),
        orderBy('expirationDate', 'asc')
      );
      const snap = await getDocs(q);
      const batchData = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setBatches(batchData);
      if (batchData.length > 0) {
        setSelectedBatch(batchData[0]); // FIFO automático
      }
    } catch (err) {
      console.error('Error cargando lotes:', err);
    }
  }, [location]);

  // Selección FIFO automática
  const autoSelectFifoBatch = (batchList) => {
    return batchList
      .filter(b => b.currentQuantity > 0 && b.isActive)
      .sort((a, b) => {
        const dateA = a.expirationDate?.toDate?.() || new Date(a.expirationDate);
        const dateB = b.expirationDate?.toDate?.() || new Date(b.expirationDate);
        return dateA - dateB;
      })[0];
  };

  // Generación automática de alertas de stock
  const checkAndCreateAlerts = async (productId, locationId) => {
    try {
      const batchesSnap = await getDocs(
        query(collection(db, 'batches'),
          where('productId', '==', productId),
          where('location', '==', locationId),
          where('isActive', '==', true))
      );

      const totalStock = batchesSnap.docs.reduce((sum, d) => sum + d.data().currentQuantity, 0);
      const productSnap = await getDoc(doc(db, 'products', productId));
      const product = productSnap.data();

      if (!product) return;

      // Alerta de stock bajo
      if (totalStock <= product.minStockLevel) {
        const existing = await getDocs(query(collection(db, 'stock_alerts'),
          where('productId', '==', productId),
          where('alertType', '==', 'low_stock'),
          where('isResolved', '==', false)));
        if (existing.empty) {
          await addDoc(collection(db, 'stock_alerts'), {
            productId, locationId, alertType: 'low_stock',
            currentStock: totalStock, threshold: product.minStockLevel,
            isResolved: false, createdAt: serverTimestamp(),
            message: `${product.name} - Stock crítico (${totalStock} ${product.unitOfMeasure})`,
          });
        }
      }

      // Alerta de próximo vencimiento (< 30 días)
      const in30Days = new Date(); in30Days.setDate(in30Days.getDate() + 30);
      batchesSnap.docs.forEach(async (batchDoc) => {
        const batch = batchDoc.data();
        const expDate = batch.expirationDate?.toDate?.() || new Date(batch.expirationDate);
        if (expDate <= in30Days) {
          const daysLeft = Math.floor((expDate - new Date()) / 86400000);
          const existing = await getDocs(query(collection(db, 'stock_alerts'),
            where('batchId', '==', batchDoc.id),
            where('alertType', '==', 'expiring'),
            where('isResolved', '==', false)));
          if (existing.empty) {
            await addDoc(collection(db, 'stock_alerts'), {
              productId, batchId: batchDoc.id, alertType: 'expiring',
              daysToExpiration: daysLeft, isResolved: false, createdAt: serverTimestamp(),
              message: `${product.name} - Lote ${batch.batchNumber} vence en ${daysLeft} días`,
            });
          }
        }
      });
    } catch (err) {
      console.error('Error creando alertas:', err);
    }
  };

  // Manejar selección de producto
  const handleProductSelect = (product) => {
    setSelectedProduct(product);
    if (product.isControlled) {
      setControlledWarning('Este producto requiere el flujo de sustancias controladas');
    } else {
      setControlledWarning('');
    }
    loadBatches(product.id);
  };

  // Manejar dispensación con writeBatch
  const handleDispense = async () => {
    if (!selectedProduct || !selectedBatch || !form.quantity) {
      setError('Faltan datos requeridos');
      return;
    }

    const quantity = parseFloat(form.quantity);
    if (quantity > selectedBatch.currentQuantity) {
      setError('La cantidad supera el stock disponible en el lote seleccionado');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const batch = writeBatch(db);

      // 1. Actualizar currentQuantity del lote
      const batchRef = doc(db, 'batches', selectedBatch.id);
      batch.update(batchRef, {
        currentQuantity: increment(-quantity),
        lastTransactionDate: serverTimestamp(),
      });

      // 2. Registrar la transacción
      const txRef = doc(collection(db, 'inventory_transactions'));
      batch.set(txRef, {
        type: 'dispensation',
        productId: selectedProduct.id,
        batchId: selectedBatch.id,
        quantity: -quantity,
        unitOfMeasure: selectedProduct.unitOfMeasure,
        location,
        referenceId: form.referenceId || referenceId,
        referenceType: form.referenceType,
        patientId: form.patientId,
        doctorId: form.doctorId,
        nurseId: form.nurseId,
        purpose: form.purpose,
        cost: selectedProduct.averageCost * quantity,
        totalCost: selectedProduct.averageCost * quantity,
        timestamp: serverTimestamp(),
        notes: form.notes,
      });

      // 3. Cargo al paciente (si aplica)
      if (form.patientId) {
        const chargeRef = doc(collection(db, 'patient_accounts', form.patientId, 'charges'));
        batch.set(chargeRef, {
          type: 'medication',
          productId: selectedProduct.id,
          batchId: selectedBatch.id,
          description: `${selectedProduct.name} - ${quantity} ${selectedProduct.unitOfMeasure}`,
          quantity,
          unitPrice: selectedProduct.sellingPrice,
          totalAmount: selectedProduct.sellingPrice * quantity,
          timestamp: serverTimestamp(),
          referenceId: form.referenceId || referenceId,
          referenceType: form.referenceType,
          doctorId: form.doctorId,
          status: 'pending',
        });
      }

      await batch.commit();
      await checkAndCreateAlerts(selectedProduct.id, location);
      setShowSuccess(true);
      
      // Resetear formulario
      setActiveStep(0);
      setSelectedProduct(null);
      setForm({
        referenceType: 'hospitalization',
        referenceId: '',
        patientId: patientId || '',
        doctorId: '',
        nurseId: '',
        purpose: 'medication',
        quantity: '',
        notes: '',
      });
      setBatches([]);
      setSelectedBatch(null);
      setSearchTerm('');
      setProducts([]);

      if (onDispenseComplete) {
        onDispenseComplete();
      }
    } catch (err) {
      console.error('Error en dispensación:', err);
      setError('Error al procesar la dispensación');
    } finally {
      setLoading(false);
    }
  };

  const handleNext = () => {
    if (activeStep === 0 && !selectedProduct) {
      setError('Selecciona un producto');
      return;
    }
    if (activeStep === 1 && !form.quantity) {
      setError('Ingresa la cantidad');
      return;
    }
    if (activeStep === 2 && !selectedBatch) {
      setError('Selecciona un lote');
      return;
    }
    setError(null);
    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  // Render de cada paso
  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Box>
            <Autocomplete
              options={products}
              getOptionLabel={(p) => `${p.name} (${p.genericName})`}
              value={selectedProduct}
              onChange={(_, p) => handleProductSelect(p)}
              onInputChange={(_, v) => handleProductSearch(v)}
              renderInput={(params) => (
                <TextField {...params} label="Medicamento o insumo" fullWidth />
              )}
              renderOption={(props, p) => (
                <Box {...props}>
                  <Typography variant="body2" fontWeight={700}>{p.name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {p.genericName} · {p.presentation}
                    {p.isControlled && <Chip label="Controlado" size="small" color="error" sx={{ ml: 1 }}/>}
                  </Typography>
                </Box>
              )}
            />
            {controlledWarning && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                {controlledWarning}
              </Alert>
            )}
          </Box>
        );

      case 1:
        return (
          <Grid container spacing={2}>
            <Grid size={12}>
              <FormControl fullWidth>
                <InputLabel shrink>Tipo de referencia</InputLabel>
                <Select
                  value={form.referenceType}
                  onChange={e => setForm({...form, referenceType: e.target.value})}
                  label="Tipo de referencia"
                >
                  <MenuItem value="hospitalization">Hospitalización</MenuItem>
                  <MenuItem value="surgery">Cirugía</MenuItem>
                  <MenuItem value="consultation">Consulta</MenuItem>
                  <MenuItem value="inventory">Inventario</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid
              size={{
                xs: 12,
                md: 6
              }}>
              <TextField
                label="Paciente"
                value={form.patientId}
                onChange={e => setForm({...form, patientId: e.target.value})}
                fullWidth
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                md: 6
              }}>
              <TextField
                label="Médico indicador"
                value={form.doctorId}
                onChange={e => setForm({...form, doctorId: e.target.value})}
                fullWidth
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                md: 6
              }}>
              <FormControl fullWidth>
                <InputLabel shrink>Propósito</InputLabel>
                <Select
                  value={form.purpose}
                  onChange={e => setForm({...form, purpose: e.target.value})}
                  label="Propósito"
                >
                  <MenuItem value="anesthesia">Anestesia</MenuItem>
                  <MenuItem value="medication">Medicación</MenuItem>
                  <MenuItem value="procedure">Procedimiento</MenuItem>
                  <MenuItem value="suture">Sutura</MenuItem>
                  <MenuItem value="other">Otro</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid
              size={{
                xs: 12,
                md: 6
              }}>
              <TextField
                label="Cantidad"
                type="number"
                value={form.quantity}
                onChange={e => setForm({...form, quantity: e.target.value})}
                fullWidth
                helperText={`Unidad: ${selectedProduct?.unitOfMeasure}`}
              />
            </Grid>
            <Grid size={12}>
              <TextField
                label="Notas"
                multiline
                rows={2}
                value={form.notes}
                onChange={e => setForm({...form, notes: e.target.value})}
                fullWidth
              />
            </Grid>
          </Grid>
        );

      case 2:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Lote seleccionado automáticamente (FIFO)
            </Typography>
            {selectedBatch && (
              <Paper sx={{ p: 2, bgcolor: '#F0FDF4', border: '1px solid #10B981' }}>
                <Typography variant="body2" fontWeight="bold">
                  Lote: {selectedBatch.batchNumber}
                </Typography>
                <Typography variant="body2">
                  Vence: {selectedBatch.expirationDate?.toDate?.().toLocaleDateString('es-CL')}
                </Typography>
                <Typography variant="body2">
                  Disponible: {selectedBatch.currentQuantity} {selectedProduct?.unitOfMeasure}
                </Typography>
              </Paper>
            )}
            <FormControlLabel
              control={
                <Switch
                  checked={useOtherBatch}
                  onChange={e => setUseOtherBatch(e.target.checked)}
                />
              }
              label="Usar otro lote"
              sx={{ mt: 2 }}
            />
            {useOtherBatch && (
              <Box sx={{ mt: 2 }}>
                <FormControl fullWidth>
                  <InputLabel shrink>Seleccionar lote</InputLabel>
                  <Select
                    value={selectedBatch?.id || ''}
                    onChange={e => setSelectedBatch(batches.find(b => b.id === e.target.value))}
                    label="Lote"
                  >
                    {batches.map(b => (
                      <MenuItem key={b.id} value={b.id}>
                        {b.batchNumber} - {b.currentQuantity} {selectedProduct?.unitOfMeasure}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  label="Justificación"
                  multiline
                  rows={2}
                  value={otherBatchReason}
                  onChange={e => setOtherBatchReason(e.target.value)}
                  fullWidth
                  sx={{ mt: 2 }}
                  required
                />
              </Box>
            )}
          </Box>
        );

      case 3:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Resumen de la dispensación
            </Typography>
            <TableContainer>
              <Table>
                <TableBody>
                  <TableRow>
                    <TableCell>Producto</TableCell>
                    <TableCell>{selectedProduct?.name}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Cantidad</TableCell>
                    <TableCell>{form.quantity} {selectedProduct?.unitOfMeasure}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Lote</TableCell>
                    <TableCell>{selectedBatch?.batchNumber}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Paciente</TableCell>
                    <TableCell>{form.patientId}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Propósito</TableCell>
                    <TableCell>{form.purpose}</TableCell>
                  </TableRow>
                  {selectedProduct?.sellingPrice && (
                    <TableRow>
                      <TableCell>Cargo al paciente</TableCell>
                      <TableCell>
                        ${(selectedProduct.sellingPrice * parseFloat(form.quantity)).toLocaleString('es-CL')} CLP
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        Dispensación de Insumos
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Stepper activeStep={activeStep} orientation="vertical">
        {steps.map((label, index) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
            <StepContent>
              <Box sx={{ py: 2 }}>
                {renderStepContent(index)}
              </Box>
              <Box sx={{ mb: 2 }}>
                <Button
                  disabled={index === 0}
                  onClick={handleBack}
                  sx={{ mt: 1, mr: 1 }}
                >
                  Atrás
                </Button>
                <Button
                  variant="contained"
                  onClick={index === steps.length - 1 ? handleDispense : handleNext}
                  sx={{ mt: 1, mr: 1 }}
                  disabled={loading}
                >
                  {index === steps.length - 1 ? 'Confirmar' : 'Siguiente'}
                  {loading && <CircularProgress size={20} sx={{ ml: 1 }} />}
                </Button>
              </Box>
            </StepContent>
          </Step>
        ))}
      </Stepper>

      <Snackbar
        open={showSuccess}
        autoHideDuration={3000}
        onClose={() => setShowSuccess(false)}
      >
        <Alert severity="success">
          Dispensación registrada correctamente
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default DispenseForm;
